/**
 * HITL-1748. Builds an option's nested modifier groups on first read.
 *
 * A DQ burger built eagerly is ~154,500 option nodes: every combo size carries
 * every drink, every Blizzard upgrade carries every mix-in. With
 * `hitl_lazy_option_build` on, `buildFullMenuUnifiedItem` builds the item's own
 * groups and their options, but each option's `modifierGroups` is left `{}` and
 * the option carries `lazyGroupsRef` — the id of the build context registered
 * here. `getOptionModifierGroups` builds that option's groups (one level, whose
 * options are lazy again) the first time anything reads them.
 *
 * Rules every caller relies on:
 * - Never writes to the option. Options are frozen in the store, and Immer
 *   drafts inside reducers.
 * - The same option always returns the same object (memoised per context by
 *   path + item id), so React memo comparisons stay stable.
 * - The level is built by the same `parseModifierGroup` as the eager build,
 *   so a lazily expanded tree equals the eager one.
 * - A context that was evicted (or never registered in this tab) is rebuilt
 *   from the current store through the provider, and logged. It never returns
 *   `{}` for an option that has groups.
 */
import logger from './logger';

type ModifierGroupsMap = Record<string, any>;

export interface LazyBuildContext {
  brandMenuItems: Record<string, any>;
  brandMenuConstraints: Record<string, any>;
  unavailableItemsByName: Record<string, any>;
  buildViaAutoCombo: boolean;
  timezone?: string;
  restaurantMealPeriodsByName?: Record<string, any>;
  currentRestaurantGroups?: string[];
  // Meal-period answers are kept for the context's lifetime, so a nested group
  // built later answers as the eager build would have.
  mealPeriodActiveCache: Map<string, boolean>;
}

export interface LazyOption {
  name: string;
  itemId: string;
  pathToItem?: string;
  modifierGroups?: ModifierGroupsMap;
  lazyGroupsRef?: string;
}

/**
 * Typed wrapper: the accessor returns the same map type the caller's option
 * carries (`GenericMap<ParsedModifierGroup>` for menu and cart nodes).
 */
export type OptionModifierGroups<T extends { modifierGroups?: unknown }> =
  NonNullable<T['modifierGroups']>;

type LevelBuilder = (
  ctx: LazyBuildContext,
  ctxId: string,
  option: LazyOption
) => ModifierGroupsMap;

type ContextProvider = (
  buildViaAutoCombo: boolean
) => LazyBuildContext | undefined;

const MAX_CONTEXTS = 8;
// A DQ burger's required paths (walked on first render) build a few thousand
// levels; the cap leaves ample room so they never churn.
const MAX_LEVELS_PER_CONTEXT = 20000;

let levelBuilder: LevelBuilder | undefined;
let contextProvider: ContextProvider | undefined;

// Insertion order doubles as recency: the first key is the least recently used.
const contexts = new Map<
  string,
  { ctx: LazyBuildContext; levels: Map<string, ModifierGroupsMap> }
>();

/** Called once by menuUnification.utils, which owns `parseModifierGroup`. */
export const registerLazyGroupBuilder = (builder: LevelBuilder) => {
  levelBuilder = builder;
};

/** Called once by the store, to rebuild an evicted context from current state. */
export const registerLazyContextProvider = (provider: ContextProvider) => {
  contextProvider = provider;
};

// Stable small ids for the object inputs, so equal inputs give equal ids.
const objectIds = new WeakMap<object, number>();
let nextObjectId = 1;
const idOf = (value: object | undefined) => {
  if (!value) return 0;
  let id = objectIds.get(value);
  if (!id) {
    id = nextObjectId++;
    objectIds.set(value, id);
  }
  return id;
};

export const lazyContextId = ({
  brandMenu,
  unavailableItemsByName,
  restaurantMealPeriodsByName,
  restaurantCode,
  timezone,
  buildViaAutoCombo,
  activeMealPeriods,
}: {
  brandMenu: object;
  unavailableItemsByName: object;
  restaurantMealPeriodsByName?: object;
  restaurantCode: string;
  timezone?: string;
  buildViaAutoCombo: boolean;
  activeMealPeriods: string;
}) =>
  [
    idOf(brandMenu),
    idOf(unavailableItemsByName),
    idOf(restaurantMealPeriodsByName),
    restaurantCode,
    timezone || '',
    activeMealPeriods,
    // Last, so the miss path can read it back.
    buildViaAutoCombo ? '1' : '0',
  ].join('|');

const touch = (ctxId: string) => {
  const entry = contexts.get(ctxId);
  if (entry) {
    contexts.delete(ctxId);
    contexts.set(ctxId, entry);
  }
  return entry;
};

/**
 * Registers (or refreshes) a context and returns it. `makeCtx` runs only if
 * the context is new.
 */
export const ensureLazyContext = (
  ctxId: string,
  makeCtx: () => LazyBuildContext
): LazyBuildContext => {
  const existing = touch(ctxId);
  if (existing) return existing.ctx;
  const ctx = makeCtx();
  contexts.set(ctxId, { ctx, levels: new Map() });
  while (contexts.size > MAX_CONTEXTS) {
    contexts.delete(contexts.keys().next().value as string);
  }
  return ctx;
};

const recoverContext = (ctxId: string, option: LazyOption) => {
  const buildViaAutoCombo = ctxId.endsWith('|1');
  const ctx = contextProvider?.(buildViaAutoCombo);
  logger.warn({
    message: `Lazy option context ${
      ctx ? 'rebuilt from current menu' : 'missing'
    } for ${option.name}`,
    optionName: option.name,
    pathToItem: option.pathToItem,
  });
  if (!ctx) return undefined;
  ensureLazyContext(ctxId, () => ctx);
  return contexts.get(ctxId);
};

/**
 * An option's nested modifier groups. For an eagerly built option (flag off,
 * legacy PRP menu, or any node without `lazyGroupsRef`) this is just
 * `option.modifierGroups`.
 */
export function getOptionModifierGroups<T extends LazyOption>(
  option: T
): OptionModifierGroups<T>;
export function getOptionModifierGroups(
  option: undefined | null
): ModifierGroupsMap;
export function getOptionModifierGroups(
  option: LazyOption | undefined | null
): ModifierGroupsMap {
  if (!option) return {};
  const ctxId = option.lazyGroupsRef;
  if (!ctxId || !levelBuilder) return option.modifierGroups || {};

  const entry = touch(ctxId) || recoverContext(ctxId, option);
  if (!entry) return option.modifierGroups || {};

  const levelKey = `${option.pathToItem || ''}\u0000${option.itemId}`;
  const built = entry.levels.get(levelKey);
  if (built) {
    // Refresh recency, so the cap evicts the least recently READ level.
    entry.levels.delete(levelKey);
    entry.levels.set(levelKey, built);
    return built;
  }

  const groups = levelBuilder(entry.ctx, ctxId, option);
  entry.levels.set(levelKey, groups);
  if (entry.levels.size > MAX_LEVELS_PER_CONTEXT) {
    entry.levels.delete(entry.levels.keys().next().value as string);
  }
  return groups;
}

export const clearLazyOptionBuildForTests = () => {
  contexts.clear();
};
